import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, 
  MessageSquare, 
  Briefcase, 
  CheckCircle,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const socketRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications?unread_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  // Socket connection for real-time notifications
  useEffect(() => {
    if (!user || !token) return;

    // Use polling first then upgrade to websocket for better reliability
    socketRef.current = io(API_URL, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected for notifications');
      // Join user's notification room
      socketRef.current.emit('join_user_room', { user_id: user.user_id });
    });

    socketRef.current.on('connect_error', (error) => {
      console.log('Socket connection error:', error.message);
    });

    socketRef.current.on('new_notification', (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico'
        });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_user_room', { user_id: user.user_id });
        socketRef.current.disconnect();
      }
    };
  }, [user, token]);

  // Initial fetch and periodic polling as fallback
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds as fallback for WebSocket issues
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    setOpen(false);
    
    // Navigate based on notification type
    if (notification.type === 'new_message' && notification.data?.chat_id) {
      navigate(`/chat/${notification.data.chat_id}`);
    } else if (notification.type === 'new_application' && notification.data?.job_id) {
      navigate(`/jobs/${notification.data.job_id}`);
    } else if (notification.type === 'application_status' && notification.data?.job_id) {
      navigate(`/jobs/${notification.data.job_id}`);
    } else if (notification.type === 'job_status' && notification.data?.job_id) {
      navigate(`/jobs/${notification.data.job_id}`);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'new_message':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'new_application':
        return <Briefcase className="w-4 h-4 text-emerald-500" />;
      case 'application_status':
        return <CheckCircle className="w-4 h-4 text-amber-500" />;
      case 'job_status':
        return <Briefcase className="w-4 h-4 text-purple-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">{t('notifications.title')}</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-emerald-600 hover:text-emerald-700"
            >
              {t('notifications.mark_all_read')}
            </button>
          )}
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>{t('notifications.no_notifications')}</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <button
                key={notification.notification_id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full flex items-start gap-3 p-4 hover:bg-slate-50 text-left transition-colors ${
                  !notification.read ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className="mt-1">{getIcon(notification.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-slate-900`}>
                    {notification.title}
                  </p>
                  <p className="text-sm text-slate-500 truncate">{notification.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{timeAgo(notification.created_at)}</p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
